import { AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { ApiError } from '@/types';

interface ErrorMessageProps {
  error: ApiError | Error | string;
  title?: string | undefined;
  onRetry?: (() => void) | undefined;
  className?: string | undefined;
}

export function ErrorMessage({
  error,
  title = 'Error',
  onRetry,
  className,
}: ErrorMessageProps) {
  // A 401 from any endpoint means the access token expired. The generic
  // "An error occurred" message is misleading — operators report it looks
  // like a server failure and they hit Retry repeatedly. Surface the actual
  // cause and the actionable next step. AuthGuard will usually take over
  // within a render, but during the race we render this fallback first.
  const isSessionExpired = isApiError(error) && error.status === 401;
  const effectiveTitle = isSessionExpired ? 'Session Expired' : title;
  const message = isSessionExpired
    ? 'Your sign-in expired. Click Retry to refresh, or sign out and back in if Retry keeps failing.'
    : getErrorMessage(error);

  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{effectiveTitle}</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>{message}</span>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="ml-4"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

function isApiError(error: ApiError | Error | string): error is ApiError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

function getErrorMessage(error: ApiError | Error | string): string {
  if (typeof error === 'string') {
    return error;
  }

  if ('detail' in error && error.detail) {
    return error.detail;
  }

  if ('title' in error) {
    return error.title;
  }

  if ('message' in error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

interface FullPageErrorProps {
  error: ApiError | Error | string;
  title?: string | undefined;
  onRetry?: (() => void) | undefined;
}

export function FullPageError({ error, title, onRetry }: FullPageErrorProps) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="max-w-md w-full">
        <ErrorMessage error={error} title={title} onRetry={onRetry} />
      </div>
    </div>
  );
}
